import type { AuthSession } from './types'
import { resolveLatestPostLoginRoute } from './session'
import { fetchAuthenticatorStatus } from '@/api/profile'

// Router state is lost when login leaves the SPA (Xero SSO, the 2FA email link), so the
// target is also kept here. Per tab, like the extension intent: a 2FA link opened in a new tab lands on the default route.

const RETURN_TO_KEY = 'di_login_return_to'
const RETURN_TO_TS_KEY = 'di_login_return_to_ts'
const RETURN_TO_TTL_MS = 30 * 60 * 1000

// Returning to these would loop back into login or straight out again.
const AUTH_PATHS = ['/login', '/logout', '/signout', '/confirm-login', '/usersignin-confirmation']

/** In-app only: a `//host` or `/\host` path is a protocol-relative URL to another site. */
export function isInAppReturnPath(path: unknown): path is string {
  if (typeof path !== 'string') return false
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false
  const pathname = (path.split(/[?#]/)[0] ?? '').toLowerCase()
  // `/` is the home redirect, so there is no page to return to; the default route applies.
  if (pathname === '/') return false
  return !AUTH_PATHS.some((authPath) => pathname === authPath || pathname.startsWith(`${authPath}/`))
}

export function rememberReturnTo(path: string | undefined): void {
  if (!isInAppReturnPath(path)) return
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path)
    sessionStorage.setItem(RETURN_TO_TS_KEY, String(Date.now()))
  } catch {
    // Storage blocked: password login still has the router-state target passed to resolvePostLoginTarget.
  }
}

/** Read once and cleared, so an abandoned attempt cannot redirect a later login. */
export function takeReturnTo(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_TO_KEY)
    const savedAt = Number(sessionStorage.getItem(RETURN_TO_TS_KEY) || 0)
    sessionStorage.removeItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_TS_KEY)
    if (!savedAt || Date.now() - savedAt > RETURN_TO_TTL_MS) return null
    return isInAppReturnPath(path) ? path : null
  } catch {
    return null
  }
}

/** Profile opens authenticator setup when it has this query param. */
export const AUTHENTICATOR_SETUP_PARAM = { key: 'setup', value: 'authenticator' } as const

/**
 * After an admin reset (or an email change) the user is sent back through authenticator
 * setup on their next sign-in. This is a reminder, not a security check, so a failed
 * status call just skips it.
 */
async function authenticatorSetupTarget(): Promise<string | null> {
  try {
    const status = await fetchAuthenticatorStatus()
    return status?.setupRequired ? `/profile?${AUTHENTICATOR_SETUP_PARAM.key}=${AUTHENTICATOR_SETUP_PARAM.value}` : null
  } catch {
    return null
  }
}

/**
 * `routerFrom` is the target in this page's router state, when login never left
 * the SPA. The stored copy is always consumed, so it cannot outlive this login.
 */
export async function resolvePostLoginTarget(session: AuthSession, routerFrom?: string): Promise<string> {
  const stored = takeReturnTo()
  const target = (isInAppReturnPath(routerFrom) ? routerFrom : null) ?? stored

  // In parallel with the default route, which also loads the permissions every route guard reads.
  const [setupTarget, defaultRoute] = await Promise.all([
    authenticatorSetupTarget(),
    resolveLatestPostLoginRoute(session).then(
      (route) => ({ route }),
      (error: unknown) => ({ error })
    ),
  ])
  if (setupTarget) {
    // Keep the user's destination for the next sign-in rather than dropping it.
    if (target) rememberReturnTo(target)
    return setupTarget
  }

  if (!target) {
    if ('error' in defaultRoute) throw defaultRoute.error
    return defaultRoute.route
  }

  // The default route was resolved even though it isn't used: it loads the permissions
  // the target's own route guard reads. A failure there doesn't block the target.
  return target
}
